import { ReactElement, useCallback } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';

import { AiContentExternalLogoSvgs, AiContentExternalNamesTypes } from './ai-content-logos';
import { MobileNavTitle } from './mobile-nav/mobile-nav-card';
import { DivOnHoverExit, useGlobalNavContext, GlobalNavEyebrow } from './nav-links-container';

const aiContentCss = css({
	display: 'grid',
	gridTemplateColumns: '1fr',
	width: '100%',
	[media.above.md]: {
		gridTemplateColumns: '2fr 1fr',
	},
});

const primaryContentCss = css({
	paddingInline: token('space.400'),
	paddingBlockStart: token('space.500'),
	[media.below.md]: {
		paddingBlockStart: token('space.0'),
		paddingInline: token('space.0'),
	},
});

const secondaryContentCss = css({
	backgroundColor: wacToken('color.neutral.light100'),
	paddingInline: token('space.400'),
	paddingBlock: token('space.500'),
	[media.below.md]: {
		marginInline: token('space.negative.400'),
	},
});

const horizontalGridCss = css({
	display: 'grid',
	alignItems: 'flex-start',
	columnGap: token('space.400'),
	gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	marginBlockStart: 0,
	marginBlockEnd: token('space.200'),
	[media.below.md]: {
		gridTemplateColumns: '1fr',
		marginBlockEnd: token('space.500'),
	},
});

const productCss = css({
	alignItems: 'flex-start',
	color: wacToken('color.primary.black'),
	display: 'flex',
	gap: token('space.100'),
	marginBlockEnd: token('space.400'),
	paddingBlockEnd: token('space.050'),
	textDecoration: 'none',
	':focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
	':hover': {
		color: wacToken('color.primary.blue'),
	},
});

const productIconCss = css({
	alignItems: 'center',
	display: 'flex',
	height: '2rem',
	justifyContent: 'center',
	width: '2rem',
});

const productTitleCss = css({
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '0.875rem',
	fontWeight: 600,
	lineHeight: 1,
});

const productDescriptionCss = css({
	color: wacToken('color.neutral.light800'),
	fontSize: '0.75rem',
	lineHeight: 1,
	marginBlockStart: token('space.050'),
});

const linkListCss = css({
	display: 'grid',
	gap: token('space.100'),
	listStyle: 'none',
	margin: 0,
	padding: 0,
});

const externalListCss = css({
	display: 'grid',
	gridTemplateColumns: '1fr 1fr',
	[media.below.md]: {
		gridTemplateColumns: '1fr',
		gap: token('space.200'),
	},
});

const worksWithCss = css({
	marginBlockStart: 0,
	marginBlockEnd: token('space.150'),
	fontSize: '0.875rem',
});

const linkCss = css({
	display: 'block',
	color: wacToken('color.primary.black'),
	fontSize: '0.875rem',
	lineHeight: 1.7,
	paddingBlock: token('space.050'),
	textDecoration: 'none',
	':focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
	':hover': {
		color: wacToken('color.primary.blue'),
	},
});

const externalAiProductCss = css({
	alignItems: 'center',
	color: wacToken('color.primary.black'),
	display: 'flex',
	fontSize: '0.875rem',
	gap: token('space.100'),
	lineHeight: 1.7,
	paddingBlock: token('space.050'),
	textDecoration: 'none',
	':focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
	':hover': {
		color: wacToken('color.primary.blue'),
	},
});

const externalAiProductIconCss = css({
	display: 'flex',
	flexShrink: 0,
	height: '1rem',
	width: '1rem',
	alignItems: 'center',
});

type InternalAiProductProps = {
	description: string;
	href: string;
	icon?: ReactElement;
	productKey: ProductKeyTypes;
	title: string;
};

const useTrackAiLinkClick = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		(label: string) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'linkButton',
				actionSubjectId: 'globalNavAiLink',
				source: 'globalNavAiDropdown',
				attributes: { label },
			}).fire();
		},
		[createAnalyticsEvent],
	);
};

type AiContentExternalInfoTypes = {
	href: string;
	icon: ReactElement;
	title: string;
};

const InternalAiProduct = ({
	description,
	href,
	icon,
	title,
	productKey,
}: InternalAiProductProps) => {
	const trackAiLinkClick = useTrackAiLinkClick();

	return (
		<a
			href={href}
			css={productCss}
			aria-label={description ? `${title}: ${description}` : title}
			onClick={() => trackAiLinkClick(title)}
			data-event="clicked"
			data-event-component="linkButton"
			data-event-container="globalNavAiDropdown"
			data-label={title}
			data-product-key={productKey}
		>
			{icon && (
				<div css={productIconCss} aria-hidden="true">
					{icon}
				</div>
			)}

			<div>
				<div css={productTitleCss}>{title}</div>
				{description && <div css={productDescriptionCss}>{description}</div>}
			</div>
		</a>
	);
};

type ExternalAiProductProps = {
	href: string;
	icon: ReactElement;
	productKey: ProductKeyTypes;
	title: string;
};

const ExternalAiProduct = ({ href, icon, productKey, title }: ExternalAiProductProps) => {
	const trackAiLinkClick = useTrackAiLinkClick();

	return (
		<li>
			<a
				href={href}
				css={externalAiProductCss}
				onClick={() => trackAiLinkClick(title)}
				data-event="clicked"
				data-event-component="linkButton"
				data-event-container="globalNavAiDropdown"
				data-label={title}
				data-product-key={productKey}
			>
				<span css={externalAiProductIconCss} aria-hidden="true">
					{icon}
				</span>
				<span>{title}</span>
			</a>
		</li>
	);
};

type AiLinkProps = {
	href: string;
	productKey: ProductKeyTypes;
	text: string;
};

const AiLink = ({ href, text, productKey }: AiLinkProps) => {
	const trackAiLinkClick = useTrackAiLinkClick();

	return (
		<li>
			<a
				href={href}
				css={linkCss}
				onClick={() => trackAiLinkClick(text)}
				data-event="clicked"
				data-event-component="linkButton"
				data-event-container="globalNavAiDropdown"
				data-label={text}
				data-product-key={productKey}
			>
				{text}
			</a>
		</li>
	);
};

export const AiContent = ({ productKey = 'home' }: { productKey?: ProductKeyTypes }) => {
	const intl = useIntl();
	const { direction } = useGlobalNavContext();
	// apps
	const rovoHref = useLocalizedHref('/software/rovo');
	const teamworkGraphHref = useLocalizedHref('/platform/teamwork-graph');

	// apps help
	const aiTrustHref = useLocalizedHref('/trust/ai');
	const rovoStartHref = useLocalizedHref('/software/rovo/start');

	// ecosystem
	const rovoConnectorsHref = useLocalizedHref('/software/rovo/connectors');
	const mcpHref = useLocalizedHref('/platform/rovo-mcp');

	// works with
	const anthropicHref = useLocalizedHref('/partnerships/anthropic/guide');
	const googleHref = useLocalizedHref('/partnerships/google/guide');
	const githubHref = useLocalizedHref('/partnerships/github/guide');
	const openAiHref = useLocalizedHref('/partnerships/openai/guide');

	const platformLinks = [
		{
			href: 'https://developer.atlassian.com/platform/forge/',
			text: intl.formatMessage({
				id: 'globalnav.v2.ai.platform.buildwithforge',
				defaultMessage: 'Build with Forge',
			}),
		},
		{
			href: aiTrustHref,
			text: intl.formatMessage({
				id: 'globalnav.v2.ai.platform.aisecurity',
				defaultMessage: 'AI security and trust',
			}),
		},
		{
			href: rovoStartHref,
			text: intl.formatMessage({
				id: 'globalnav.v2.ai.platform.rovogetstarted',
				defaultMessage: 'Get started',
			}),
		},
	];

	const resourceLinks = [
		{
			href: 'https://www.atlassian.com/blog/company-news/ai-sdlc',
			text: intl.formatMessage({
				id: 'globalnav.v2.ai.resources.evolvingjira',
				defaultMessage: 'Evolving Jira for AI-native software development',
			}),
		},
		{
			href: 'https://www.atlassian.com/enterprise/success/fde',
			text: intl.formatMessage({
				id: 'globalnav.v2.ai.resources.forwarddeployed',
				defaultMessage: 'Forward Deployed Engineering',
			}),
		},
		{
			href: 'https://www.atlassian.com/blog/category/ai',
			text: intl.formatMessage({
				id: 'globalnav.v2.ai.resources.aiblogsnews',
				defaultMessage: 'AI blogs & news',
			}),
		},
		{
			href: 'https://www.atlassian.com/blog/ai-research',
			text: intl.formatMessage({
				id: 'globalnav.v2.ai.resources.airesearch',
				defaultMessage: 'AI research',
			}),
		},
	];

	const AiContentExternalLinksInfo: Record<
		AiContentExternalNamesTypes,
		AiContentExternalInfoTypes
	> = {
		anthropic: {
			title: 'Anthropic',
			href: anthropicHref,
			icon: AiContentExternalLogoSvgs.anthropic,
		},
		google: {
			title: 'Google',
			href: googleHref,
			icon: AiContentExternalLogoSvgs.google,
		},
		github: {
			title: 'GitHub',
			href: githubHref,
			icon: AiContentExternalLogoSvgs.github,
		},
		openAi: {
			title: 'OpenAI',
			href: openAiHref,
			icon: AiContentExternalLogoSvgs.openAi,
		},
	};

	return (
		<DivOnHoverExit>
			<div css={aiContentCss} data-direction={direction}>
				<div css={primaryContentCss}>
					<MobileNavTitle
						title={intl.formatMessage({
							id: 'globalnav.v2.ai.mobiletitle',
							defaultMessage: 'AI',
						})}
					/>

					<GlobalNavEyebrow>
						{intl.formatMessage({ id: 'globalnav.ai.platform', defaultMessage: 'AI platform' })}
					</GlobalNavEyebrow>
					<div css={horizontalGridCss}>
						<div>
							<InternalAiProduct
								title={intl.formatMessage({
									id: 'globalnav.ai.rovo.title',
									defaultMessage: 'Rovo',
								})}
								description={intl.formatMessage({
									id: 'globalnav.ai.rovo.description',
									defaultMessage: 'AI that knows your business',
								})}
								href={rovoHref}
								icon={
									<LogoV3 brand="rovo" type="mark" appearance="brand" width="32px" height="32px" />
								}
								productKey={productKey}
							/>
							<InternalAiProduct
								title={intl.formatMessage({
									id: 'globalnav.ai.teamworkGraph.title',
									defaultMessage: 'Teamwork Graph',
								})}
								description={intl.formatMessage({
									id: 'globalnav.ai.teamworkGraph.description',
									defaultMessage: 'Context wherever you work',
								})}
								href={teamworkGraphHref}
								icon={
									<LogoV3
										brand="teamworkgraph"
										type="mark"
										appearance="brand"
										width="32px"
										height="32px"
									/>
								}
								productKey={productKey}
							/>
						</div>
						<div>
							<ul css={linkListCss}>
								{platformLinks.map((link) => (
									<AiLink key={link.text} {...link} productKey={productKey} />
								))}
							</ul>
						</div>
					</div>
					<GlobalNavEyebrow>
						{intl.formatMessage({ id: 'globalnav.ai.ecosystem', defaultMessage: 'AI ecosystem' })}
					</GlobalNavEyebrow>
					<div css={horizontalGridCss}>
						<div>
							<InternalAiProduct
								title={intl.formatMessage({
									id: 'globalnav.ai.rovoconnectors.title',
									defaultMessage: 'Rovo Connectors',
								})}
								description={intl.formatMessage({
									id: 'globalnav.ai.rovoconnectors.description',
									defaultMessage: 'Centralize your knowledge sources',
								})}
								href={rovoConnectorsHref}
								productKey={productKey}
							/>

							<InternalAiProduct
								title={intl.formatMessage({
									id: 'globalnav.ai.atlassianmcp.title',
									defaultMessage: 'Atlassian MCP',
								})}
								description={intl.formatMessage({
									id: 'globalnav.ai.atlassianmcp.description',
									defaultMessage: 'Bring Atlassian into your AI tools',
								})}
								href={mcpHref}
								productKey={productKey}
							/>
						</div>
						<div>
							<div css={worksWithCss}>
								{intl.formatMessage({
									id: 'globalnav.ai.workswith',
									defaultMessage: 'Works with:',
								})}
							</div>
							<ul css={[linkListCss, externalListCss]}>
								{Object.entries(AiContentExternalLinksInfo).map(([name, provider]) => (
									<ExternalAiProduct key={name} {...provider} productKey={productKey} />
								))}
							</ul>
						</div>
					</div>
				</div>
				<div css={secondaryContentCss}>
					<GlobalNavEyebrow>
						{intl.formatMessage({
							id: 'globalnav.ai.resources',
							defaultMessage: 'Latest news and resources',
						})}
					</GlobalNavEyebrow>
					<ul css={linkListCss}>
						{resourceLinks.map((link) => (
							<AiLink key={link.text} {...link} productKey={productKey} />
						))}
					</ul>
				</div>
			</div>
		</DivOnHoverExit>
	);
};
